import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, RefreshCw, Inbox, Sparkles, CalendarCheck, Trophy, Loader2,
  ArrowLeft, Mail, Phone, Clock, Globe2, Target, User2, ShieldAlert,
  StickyNote, ChevronDown, MailCheck, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import { AdminNav } from "../components/admin/AdminNav";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";

const STATUSES = [
  { key: "new", label: "New", color: "border-gold/40 bg-gold/15 text-gold-dark" },
  { key: "contacted", label: "Contacted", color: "border-sky-200 bg-sky-50 text-sky-700" },
  { key: "call_scheduled", label: "Call Scheduled", color: "border-forest/25 bg-forest/10 text-forest" },
  { key: "converted", label: "Converted", color: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  { key: "not_a_fit", label: "Not a Fit", color: "border-stone-200 bg-stone-100 text-stone-500" },
];

const statusMeta = (key) => STATUSES.find((s) => s.key === key) || STATUSES[0];

const fmtDate = (iso) => {
  try {
    return format(new Date(iso), "MMM d, yyyy · h:mm a");
  } catch {
    return "—";
  }
};

function StatusBadge({ status, onChange, testId }) {
  const meta = statusMeta(status);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
        className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wider outline-none transition-transform active:scale-95 ${meta.color}`}
      >
        {meta.label} <ChevronDown className="h-3 w-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48 rounded-2xl border-border bg-white p-1.5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {STATUSES.map((s) => (
          <DropdownMenuItem
            key={s.key}
            data-testid={`${testId}-opt-${s.key}`}
            onClick={() => onChange(s.key)}
            className={`cursor-pointer rounded-xl px-3 py-2 text-xs font-semibold focus:bg-secondary ${
              s.key === status ? "bg-secondary" : ""
            }`}
          >
            {s.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function GuardShell({ icon: Icon, title, copy, children, testId }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bone px-4 pt-[72px]" data-testid={testId}>
      <div className="w-full max-w-md rounded-[2rem] border border-border bg-white p-10 text-center shadow-[0_25px_70px_rgba(10,41,30,0.08)]">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold/15 text-gold-dark">
          <Icon className="h-6 w-6" />
        </span>
        <h1 className="mt-5 font-display text-2xl font-extrabold text-forest-deep">{title}</h1>
        <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{copy}</p>
        {children}
      </div>
    </div>
  );
}

export default function AdminLeadsPage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const isAdmin = !!user && user.role === "admin";

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
      setLoadError("");
    } catch (e) {
      setLoadError(formatApiError(e));
      setLeads([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load(true);
  }, [isAdmin, load]);

  const sources = useMemo(
    () => [...new Set((leads || []).map((l) => l.source).filter(Boolean))].sort(),
    [leads]
  );

  const counts = useMemo(() => {
    const c = { all: (leads || []).length };
    STATUSES.forEach((s) => (c[s.key] = 0));
    (leads || []).forEach((l) => {
      c[l.status] = (c[l.status] || 0) + 1;
    });
    return c;
  }, [leads]);

  const filtered = useMemo(
    () =>
      (leads || []).filter((l) => {
        if (statusFilter !== "all" && l.status !== statusFilter) return false;
        if (sourceFilter !== "all" && l.source !== sourceFilter) return false;
        if (query) {
          const q = query.toLowerCase();
          return [l.name, l.email, l.goal, l.phone]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q));
        }
        return true;
      }),
    [leads, query, statusFilter, sourceFilter]
  );

  const updateStatus = async (lead, status) => {
    if (lead.status === status) return;
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? data : l)));
      setSelected((s) => (s && s.id === lead.id ? data : s));
      toast.success(`${lead.name.split(" ")[0]} marked as ${statusMeta(status).label}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openLead = (lead) => {
    setSelected(lead);
    setNotesDraft(lead.notes || "");
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/leads/${selected.id}`, { notes: notesDraft });
      setLeads((ls) => ls.map((l) => (l.id === selected.id ? data : l)));
      setSelected(data);
      toast.success("Notes saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNotes(false);
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone pt-[72px]" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  if (user === false) {
    return (
      <GuardShell icon={Inbox} title="Sign in required" testId="admin-login-required"
        copy="The leads inbox is private. Sign in with your admin account to continue.">
        <Link to="/login" data-testid="admin-goto-login-btn"
          className="mt-6 inline-block rounded-full bg-forest px-8 py-3.5 font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5">
          Go to login
        </Link>
      </GuardShell>
    );
  }

  if (!isAdmin) {
    return (
      <GuardShell icon={ShieldAlert} title="Admins only" testId="admin-access-denied"
        copy={`You're signed in as ${user.email}, which doesn't have admin access to the leads inbox.`}>
        <Link to="/" data-testid="admin-back-home-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground/70 transition-colors hover:text-forest">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
      </GuardShell>
    );
  }

  const statCards = [
    { icon: Inbox, label: "Total Leads", value: counts.all, testId: "stat-total" },
    { icon: Sparkles, label: "New", value: counts.new, testId: "stat-new" },
    { icon: CalendarCheck, label: "Calls Scheduled", value: counts.call_scheduled, testId: "stat-scheduled" },
    { icon: Trophy, label: "Converted", value: counts.converted, testId: "stat-converted",
      extra: counts.all ? `${Math.round((counts.converted / counts.all) * 100)}% rate` : null },
  ];

  return (
    <div className="min-h-screen bg-bone pt-[72px]" data-testid="admin-leads-page">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Admin — CRM</p>
              <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-forest-deep sm:text-4xl">
                Leads Inbox
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Every strategy-call request in one place. Click any lead for full details and notes.
              </p>
            </div>
            <button onClick={() => load()} data-testid="leads-refresh-btn" disabled={refreshing}
              className="flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-forest transition-colors hover:border-forest/40 disabled:opacity-60">
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          <AdminNav />
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="leads-stats">
            {statCards.map((s) => (
              <div key={s.label} data-testid={s.testId}
                className="rounded-3xl border border-border bg-white p-5 transition-shadow duration-300 hover:shadow-[0_14px_40px_rgba(10,41,30,0.08)]">
                <div className="flex items-center justify-between">
                  <s.icon className="h-5 w-5 text-gold-dark" />
                  {s.extra && <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-600">{s.extra}</span>}
                </div>
                <p className="mt-4 font-display text-3xl font-extrabold text-forest-deep">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.14}>
          <div className="mt-8 flex flex-col gap-3.5 lg:flex-row lg:items-center" data-testid="leads-filters">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="leads-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, goal, phone…"
                className="h-12 w-full rounded-full border border-input bg-white pl-11 pr-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button data-testid="leads-filter-all" onClick={() => setStatusFilter("all")}
                className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                  statusFilter === "all" ? "border-forest bg-forest text-bone" : "border-border bg-white text-foreground/60 hover:border-forest/40"
                }`}>
                All · {counts.all}
              </button>
              {STATUSES.map((s) => (
                <button key={s.key} data-testid={`leads-filter-${s.key}`} onClick={() => setStatusFilter(s.key)}
                  className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                    statusFilter === s.key ? "border-forest bg-forest text-bone" : "border-border bg-white text-foreground/60 hover:border-forest/40"
                  }`}>
                  {s.label} · {counts[s.key] || 0}
                </button>
              ))}
              <select
                data-testid="leads-source-filter"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="h-10 rounded-full border border-input bg-white px-4 text-xs font-semibold text-foreground/70 outline-none focus:border-gold"
              >
                <option value="all">All sources</option>
                {sources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-6 overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
            {leads === null ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-7 w-7 animate-spin text-forest" />
              </div>
            ) : loadError ? (
              <div className="py-24 text-center" data-testid="leads-error">
                <p className="text-sm font-medium text-destructive">{loadError}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-24 text-center" data-testid="leads-empty-state">
                <Inbox className="mx-auto h-10 w-10 text-forest/20" />
                <p className="mt-4 font-display text-lg font-bold text-forest-deep">No leads match</p>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {counts.all === 0
                    ? "New strategy-call requests will appear here the moment they arrive."
                    : "Try a different search or filter."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px] text-left" data-testid="leads-table">
                  <thead>
                    <tr className="border-b border-border bg-secondary/40">
                      {["Lead", "Goal", "Timeline", "Source", "Received", "Status"].map((h) => (
                        <th key={h} className="px-6 py-4 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((lead) => (
                      <tr
                        key={lead.id}
                        data-testid={`lead-row-${lead.id}`}
                        onClick={() => openLead(lead)}
                        className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-bone/70"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest/10 font-display text-sm font-bold text-forest">
                              {(lead.name || "?")[0].toUpperCase()}
                            </span>
                            <div>
                              <p className="text-sm font-bold text-forest-deep">{lead.name}</p>
                              <p className="text-xs text-muted-foreground">{lead.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-foreground/80">{lead.goal}</p>
                          {lead.role && <p className="text-xs text-muted-foreground">{lead.role}</p>}
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground/70">{lead.timeline || "—"}</td>
                        <td className="px-6 py-4">
                          <span className="rounded-full bg-secondary px-3 py-1 font-mono text-[10px] font-medium text-forest/70">
                            {lead.source}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-xs text-muted-foreground">
                          {fmtDate(lead.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={lead.status} testId={`lead-status-${lead.id}`}
                            onChange={(s) => updateStatus(lead, s)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Reveal>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto bg-bone p-0 sm:max-w-md" data-testid="lead-detail-sheet">
          {selected && (
            <div>
              <div className="bg-forest-deep px-7 py-7">
                <SheetTitle className="sr-only">Lead details — {selected.name}</SheetTitle>
                <div className="flex items-center gap-4">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold font-display text-xl font-extrabold text-forest-deep">
                    {(selected.name || "?")[0].toUpperCase()}
                  </span>
                  <div>
                    <p className="font-display text-xl font-bold text-bone">{selected.name}</p>
                    <p className="text-xs text-emerald-100/60">{fmtDate(selected.created_at)}</p>
                  </div>
                </div>
                <div className="mt-5">
                  <StatusBadge status={selected.status} testId="lead-detail-status"
                    onChange={(s) => updateStatus(selected, s)} />
                </div>
              </div>

              <div className="space-y-6 px-7 py-7">
                <div className="space-y-3">
                  <a href={`mailto:${selected.email}`} data-testid="lead-detail-email"
                    className="flex items-center gap-3 rounded-2xl border border-border bg-white px-4 py-3.5 text-sm font-medium text-forest transition-colors hover:border-gold/50">
                    <Mail className="h-4 w-4 shrink-0 text-gold-dark" /> {selected.email}
                  </a>
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`} data-testid="lead-detail-phone"
                      className="flex items-center gap-3 rounded-2xl border border-border bg-white px-4 py-3.5 text-sm font-medium text-forest transition-colors hover:border-gold/50">
                      <Phone className="h-4 w-4 shrink-0 text-gold-dark" /> {selected.phone}
                    </a>
                  )}
                  <p className="flex items-center gap-2.5 px-1 text-xs text-muted-foreground">
                    <MailCheck className={`h-4 w-4 ${selected.confirmation_email_sent ? "text-emerald-600" : "text-stone-400"}`} />
                    Confirmation email {selected.confirmation_email_sent ? "sent" : "not sent yet"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3" data-testid="lead-detail-fields">
                  {[
                    { icon: Target, label: "Goal", value: selected.goal },
                    { icon: User2, label: "Role", value: selected.role },
                    { icon: Layers, label: "Stage", value: selected.current_stage },
                    { icon: Clock, label: "Timeline", value: selected.timeline },
                    { icon: Globe2, label: "Destination", value: selected.country },
                    { icon: CalendarCheck, label: "Call time", value: selected.preferred_time },
                  ].map((f) => (
                    <div key={f.label} className="rounded-2xl border border-border bg-white p-4">
                      <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                        <f.icon className="h-3 w-3 text-gold-dark" /> {f.label}
                      </p>
                      <p className="mt-2 text-sm font-semibold text-forest-deep">{f.value || "—"}</p>
                    </div>
                  ))}
                </div>

                <div className="rounded-2xl border border-border bg-white p-4">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Lead source</p>
                  <p className="mt-2 font-mono text-sm font-semibold text-forest">{selected.source}</p>
                </div>

                <div>
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">
                    <StickyNote className="h-3.5 w-3.5" /> Internal notes
                  </p>
                  <textarea
                    data-testid="lead-notes-input"
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={4}
                    placeholder="Call outcome, follow-ups, next steps…"
                    className="mt-3 w-full rounded-2xl border border-input bg-white p-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25"
                  />
                  <button onClick={saveNotes} data-testid="lead-notes-save-btn" disabled={savingNotes}
                    className="mt-3 flex items-center gap-2 rounded-full bg-forest px-6 py-3 font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                    {savingNotes && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save notes
                  </button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
